import axios from 'axios';

// Get base URL from environment variables or use default
//const BASE_URL = /* process.env.REACT_APP_API_BASE_URL || */ 'http://localhost:8092';
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://bloomnav.com';
// Create axios instance with base URL
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    // Add auth token if it exists
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle common errors
    if (error.response) {
      // Handle 401 Unauthorized
      if (error.response.status === 401) {
        // Logout user or refresh token
        localStorage.removeItem('authToken');
        // Redirect to login if needed
        // window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Create an additional method for file uploads
api.uploadFile = async (endpoint, formData, config = {}) => {
  // Merge with default headers but allow Content-Type to be overridden
  const mergedConfig = {
    ...config,
    headers: {
      ...config.headers,
      'X-Request-Source': 'complaint-form'
    }
  };
  
  try {
    const response = await api.post(endpoint, formData, mergedConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default api; 
 
// Convenience helpers
export const ItemsAPI = {
  async list(params = {}) {
    const { data } = await api.get('/api/items', { params });
    return data;
  },
  async create(payload) {
    const { data } = await api.post('/api/items', payload);
    return data;
  },
  async update(id, payload) {
    const { data } = await api.put(`/api/items/${id}`, payload);
    return data;
  },
  async timeline(id, params = {}) {
    const { data } = await api.get(`/api/items/${id}/inventory-timeline`, { params });
    return data;
  }
};

export const SeriesAPI = {
  async list(params = {}) {
    const { data } = await api.get('/api/series', { params });
    return data;
  },
  async create(payload = {}) {
    const { data } = await api.post('/api/series', payload);
    return data;
  },
  async update(id, payload = {}) {
    const { data } = await api.put(`/api/series/${id}`, payload);
    return data;
  },
  async current(companyId, documentType = 'INVOICE', date) {
    const { data } = await api.get('/api/series/current', { params: { companyId, documentType, date } });
    return data;
  },
  async setFormat({ companyId, documentType = 'INVOICE', prefix, suffix = '', date }) {
    const { data } = await api.post('/api/series/set-format', { companyId, documentType, prefix, suffix, date });
    return data;
  }
};

// Frontend-only storage for Order Templates until backend endpoints exist
export const OrderTemplatesAPI = {
  async list(params = {}) {
    const { data } = await api.get('/api/order-templates', { params });
    return data;
  },
  async get(id) {
    const { data } = await api.get(`/api/order-templates/${id}`);
    return data;
  },
  async create(payload) {
    const { data } = await api.post('/api/order-templates', payload);
    return data;
  },
  async update(id, payload) {
    const { data } = await api.put(`/api/order-templates/${id}`, payload);
    return data;
  },
  async remove(id) {
    const { data } = await api.delete(`/api/order-templates/${id}`);
    return data;
  }
};

export const PublicOrderTemplatesAPI = {
  async get(id) {
    const { data } = await api.get(`/public/order-templates/${id}`);
    return data;
  },
  async getBySlug(companySlug, templateSlug) {
    const { data } = await api.get(`/public/order-templates/by-slug/${companySlug}/${templateSlug}`);
    return data;
  }
};

export const PublicOrdersAPI = {
  async placeOrder(payload) {
    const { data } = await api.post('/public/order-templates/place-order', payload);
    return data;
  }
};

export const CustomersPublicAPI = {
  async getByEmail(email, companyId) {
    const { data } = await api.get('/api/customers', { params: { email, companyId } });
    return data;
  },
  async getByPhone(phone, companyId) {
    const { data } = await api.get('/api/customers', { params: { phone, companyId } });
    return data;
  },
  async getByIdentifier({ email, phone }, companyId) {
    const params = { companyId };
    if (email) params.email = email;
    if (phone) params.phone = phone;
    const { data } = await api.get('/api/customers', { params });
    return data;
  },
  async create(payload) {
    const { data } = await api.post('/api/customers', payload);
    return data;
  }
};

export const OrdersAPI = {
  async list(params = {}) {
    const { data } = await api.get('/api/orders', { params });
    return data;
  },
  async get(id) {
    const { data } = await api.get(`/api/orders/${id}`);
    return data;
  },
  async accept(id) {
    const { data } = await api.post(`/api/orders/${id}/accept`);
    return data;
  },
  async start(id) {
    const { data } = await api.post(`/api/orders/${id}/start`);
    return data;
  },
  async complete(id) {
    const { data } = await api.post(`/api/orders/${id}/complete`);
    return data;
  },
  async reject(id) {
    const { data } = await api.post(`/api/orders/${id}/reject`);
    return data;
  },
  async markPaid(id, payload = {}) {
    const { data } = await api.post(`/api/orders/${id}/mark-paid`, payload);
    return data;
  },
  async addTask(id, taskId) {
    const { data } = await api.post(`/api/orders/${id}/add-task`, { taskId });
    return data;
  }
};

export const LocationTrackingSettingsAPI = {
  async get(companyId) {
    const { data } = await api.get(`/api/companies/${companyId}/location-tracking-settings`);
    return data;
  },
  async update(companyId, payload) {
    const { data } = await api.put(`/api/companies/${companyId}/location-tracking-settings`, payload);
    return data;
  }
};

export const DocumentDefaultsAPI = {
  async get(companyId, documentType = 'QUOTE') {
    const { data } = await api.get(`/api/companies/${companyId}/document-defaults/${documentType}`);
    return data;
  },
  async upsert(companyId, documentType = 'QUOTE', payload = {}) {
    const { data } = await api.put(`/api/companies/${companyId}/document-defaults/${documentType}`, payload);
    return data;
  }
};

export const FeatureAccessAPI = {
  async myFeatures() {
    const { data } = await api.get('/api/me/feature-access');
    return data;
  },
  async listFeatures() {
    const { data } = await api.get('/api/features');
    return data;
  },
  async getPolicies(companyId) {
    const { data } = await api.get(`/api/companies/${companyId}/feature-policies`);
    return data;
  },
  async putPolicies(companyId, policies) {
    const { data } = await api.put(`/api/companies/${companyId}/feature-policies`, { companyId, policies });
    return data;
  }
};

// Expenses (OCR + CRUD)
export const ExpensesAPI = {
  async extract(payload) {
    const { data } = await api.post('/api/expenses/extract', payload);
    return data;
  },
  async bulkExtract(files = [], options = {}) {
    const { data } = await api.post('/api/expenses/bulk-extract', {
      files,
      ...(options?.usagePaymentId ? { usagePaymentId: options.usagePaymentId } : {})
    });
    return data;
  },
  async bulkUpload(files = [], options = {}) {
    const { data } = await api.post('/api/expenses/bulk-upload', {
      files,
      ...(options?.usagePaymentId ? { usagePaymentId: options.usagePaymentId } : {})
    });
    return data;
  },
  async getBulkUploadStatus(batchId) {
    const { data } = await api.get(`/api/expenses/bulk-upload/${batchId}`);
    return data;
  },
  async create(payload) {
    const { data } = await api.post('/api/expenses', payload);
    return data;
  },
  async list(params = {}) {
    const { data } = await api.get('/api/expenses', { params });
    return data;
  },
  async calendar(params = {}) {
    const { data } = await api.get('/api/expenses', {
      params: { ...params, includeProjected: true, view: 'calendar' }
    });
    return data;
  },
  async getById(id) {
    const { data } = await api.get(`/api/expenses/${id}`);
    return data;
  },
  async update(id, payload) {
    const { data } = await api.put(`/api/expenses/${id}`, payload);
    return data;
  },
  async approve(id) {
    const { data } = await api.post(`/api/expenses/${id}/approve`);
    return data;
  },
  async reject(id, payload = {}) {
    const { data } = await api.post(`/api/expenses/${id}/reject`, payload);
    return data;
  },
  async markPaid(id, payload = {}) {
    const { data } = await api.post(`/api/expenses/${id}/mark-paid`, payload);
    return data;
  },
  async generateDescription(payload = {}) {
    const { data } = await api.post('/api/expenses/generate-description', payload);
    return data;
  },
  async checkDuplicates(hashes = []) {
    const { data } = await api.post('/api/expenses/check-duplicates', { hashes });
    return data;
  }
};

export const StatementsAPI = {
  async upload(payload) {
    const { data } = await api.post('/api/statements/upload', payload);
    return data;
  },
  async list(params = {}) {
    const { data } = await api.get('/api/statements', { params });
    return data;
  },
  async getById(id) {
    const { data } = await api.get(`/api/statements/${id}`);
    return data;
  },
  async runMatch(id) {
    const { data } = await api.post(`/api/statements/${id}/match`);
    return data;
  },
  async confirmMatch(txnId, payload = {}) {
    const { data } = await api.post(`/api/statement-transactions/${txnId}/confirm-match`, payload);
    return data;
  },
  async rejectMatch(txnId) {
    const { data } = await api.post(`/api/statement-transactions/${txnId}/reject-match`);
    return data;
  },
  async checkDuplicates(hashes = []) {
    const { data } = await api.post('/api/statements/check-duplicates', { hashes });
    return data;
  }
};

export const UsageAPI = {
  async getAccount() {
    const { data } = await api.get('/api/usage/account');
    return data;
  },
  async calculate(payload = {}) {
    const { data } = await api.post('/api/usage/calculate', payload);
    return data;
  },
  async initiatePayment(payload = {}) {
    const { data } = await api.post('/api/usage/initiate-payment', payload);
    return data;
  },
  async getPaymentStatus(orderId) {
    const { data } = await api.get(`/api/usage/payment/${orderId}/status`);
    return data;
  }
};

export const RecurringExpensesAPI = {
  async create(payload) {
    const { data } = await api.post('/api/recurring-expenses', payload);
    return data;
  },
  async list(params = {}) {
    const { data } = await api.get('/api/recurring-expenses', { params });
    return data;
  },
  async getById(id) {
    const { data } = await api.get(`/api/recurring-expenses/${id}`);
    return data;
  },
  async update(id, payload) {
    const { data } = await api.put(`/api/recurring-expenses/${id}`, payload);
    return data;
  },
  async setStatus(id, status) {
    const { data } = await api.post(`/api/recurring-expenses/${id}/status`, { status });
    return data;
  },
  async occurrenceSeed(id, date) {
    const { data } = await api.get(`/api/recurring-expenses/${id}/occurrence-seed`, { params: { date } });
    return data;
  }
};

export const VendorsAPI = {
  async list(params = {}) {
    const { data } = await api.get('/api/vendors', { params });
    return data;
  },
  async getById(id) {
    const { data } = await api.get(`/api/vendors/${id}`);
    return data;
  }
};

export const CompaniesAPI = {
  async users() {
    const { data } = await api.get('/api/companies/users');
    return data;
  },
  async employees(companyId) {
    const { data } = await api.get(`/api/companies/${companyId}/employees`);
    return data;
  }
};

export const CompanyPlanAPI = {
  async myPlan() {
    const { data } = await api.get('/api/me/plan');
    return data;
  },
  async get(companyId) {
    const { data } = await api.get(`/api/companies/${companyId}/plan`);
    return data;
  },
  async update(companyId, plan) {
    const { data } = await api.put(`/api/companies/${companyId}/plan`, plan);
    return data;
  },
  async previewChange(companyId, desiredPlan) {
    const { data } = await api.post(`/api/companies/${companyId}/plan/preview`, desiredPlan);
    return data;
  },
  async initiatePayment(companyId, desiredPlan) {
    const { data } = await api.post(`/api/companies/${companyId}/plan/pay`, desiredPlan);
    return data;
  },
  async payments(companyId) {
    const { data } = await api.get(`/api/companies/${companyId}/plan/payments`);
    return data;
  },
  async history(companyId) {
    const { data } = await api.get(`/api/companies/${companyId}/plan/history`);
    return data;
  }
};

// Departments and Org helpers
export const DepartmentsAPI = {
  async users(departmentId) {
    const { data } = await api.get(`/api/departments/${departmentId}/users`);
    return data;
  },
  async employees(departmentId) {
    const { data } = await api.get(`/api/departments/${departmentId}/employees`);
    return data;
  },
  async employeesBySupervisor(supervisorId) {
    const { data } = await api.get(`/api/supervisors/${supervisorId}/employees`);
    return data;
  }
};